// react
import { useState } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import IntegrationInstructionsRoundedIcon from '@mui/icons-material/IntegrationInstructionsRounded';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

// material ui
import {
  Badge,
  Box,
  Button,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportExcel,
  ExportPrint,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid-premium';

// shared components
import PresetSelector from '@/components/ui/datagrid/PresetSelector';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

// relative
import HeatingPanicButton from '../HeatingPanicButton';

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState?.expanded ? 0 : 1,
  pointerEvents: ownerState?.expanded ? 'none' : 'auto',
  transition: theme.transitions.create(['opacity']),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState?.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState?.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

const BuildingDataGridToolbar = ({ onCreateBuilding, ...props }) => {
  const { t } = useTranslation(['common']);
  const account = useSelector(getSelectedAccount);

  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  return (
    <Toolbar>
      {/* Quick filter (animated expand-on-click) */}
      <StyledQuickFilter>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title={t('datagrid.toolbar.search')} enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{ expanded: state.expanded }}
                color="default"
                aria-disabled={state.expanded}
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              inputRef={ref}
              aria-label={t('datagrid.toolbar.search')}
              placeholder={t('datagrid.toolbar.searchPlaceholder')}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: state.value ? (
                  <InputAdornment position="end">
                    <QuickFilterClear
                      edge="end"
                      size="small"
                      aria-label={t('datagrid.toolbar.searchClear')}
                      material={{ sx: { marginRight: -0.75 } }}
                    >
                      <CancelIcon fontSize="small" />
                    </QuickFilterClear>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />
      </StyledQuickFilter>

      <Box sx={{ ml: 1 }}>
        <Button
          startIcon={<IntegrationInstructionsRoundedIcon />}
          endIcon={<ArrowForwardRoundedIcon />}
          variant="outlined"
          component={Link}
          to={`/$accountId/operations/predictive-heat-control/subscriptions`}
          sx={{
            borderRadius: 2,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textTransform: 'none',
            color: 'text.primary',
            ':hover': {
              borderColor: 'divider',
            },
          }}
        >
          {t('actions.goToItem', {
            item: t('items.subscription.indefinitePlural', { ns: 'glossary' }),
          })}
        </Button>
      </Box>

      <Box sx={{ ml: 1 }}>
        <PresetSelector {...props} />
      </Box>

      {/* Spacer pushes everything after to the right */}
      <span style={{ flex: 1 }} />

      {/* Columns panel trigger */}
      <Tooltip title={t('datagrid.toolbar.columns')}>
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>

      {/* Export */}
      <Tooltip title={t('datagrid.toolbar.export')}>
        <ToolbarButton
          id="export-menu-trigger"
          aria-controls="export-menu"
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? 'true' : undefined}
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
        >
          <FileDownloadIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>

      <Menu
        id="export-menu"
        anchorEl={exportMenuAnchor}
        open={exportMenuOpen}
        onClose={() => setExportMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ExportExcel
          render={<MenuItem />}
          onClick={() => setExportMenuAnchor(null)}
        >
          {t('datagrid.toolbar.exportExcel')}
        </ExportExcel>
        <ExportCsv
          render={<MenuItem />}
          onClick={() => setExportMenuAnchor(null)}
        >
          {t('datagrid.toolbar.exportCsv')}
        </ExportCsv>
        <ExportPrint
          render={<MenuItem />}
          onClick={() => setExportMenuAnchor(null)}
        >
          {t('datagrid.toolbar.exportPrint')}
        </ExportPrint>
      </Menu>

      {/* Filter panel trigger */}
      <Tooltip title={t('datagrid.toolbar.filters')}>
        <FilterPanelTrigger
          render={(props, state) => (
            <ToolbarButton {...props} color="default">
              <Badge
                badgeContent={state.filterCount}
                color="primary"
                variant="dot"
              >
                <FilterAltIcon fontSize="small" />
              </Badge>
            </ToolbarButton>
          )}
        />
      </Tooltip>

      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        sx={{ mx: 0.5 }}
      />

      <HeatingPanicButton accountId={account?.AccountId} />

      <Button
        startIcon={<AddRoundedIcon />}
        variant="contained"
        onClick={onCreateBuilding}
        sx={{ ml: 1 }}
      >
        {t('actions.createItem', {
          item: t('items.building.indefinite', { ns: 'glossary' }),
        })}
      </Button>
    </Toolbar>
  );
};

export default BuildingDataGridToolbar;
